import * as _ from 'lodash';
import moment from 'moment';
import type { VoidFunctionComponent } from 'react';

import { BillingTermType } from '../../../../../shared/model-typings';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useRemoveBillingTerm } from '../../../../hooks/api/billing/useRemoveBillingTerm';
import Badge from '../../../generic/Badge/Badge';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import { LoaderButton } from '../../../generic/Button/index';
import { icons } from '../../../generic/Icon/icons';
import List from '../../../generic/List';
import type { BillingTermWithUsageFields } from '../../../../../shared/model-typings/billingterm';

type Props = {
  billingTerm: BillingTermWithUsageFields;
};

const BillingTermRow: VoidFunctionComponent<Props> = ({ billingTerm }) => {
  const { mutate: removeBillingTerm, isPending } = useRemoveBillingTerm();
  const { onConfirm } = useDialogContext();

  const {
    id,
    quantity,
    usage,
    createdBy,
    isActive,
    type,
  } = billingTerm;

  const termStart = moment(billingTerm.termStart).utc().format('MM/DD/YYYY');
  const termEnd = moment(billingTerm.termEnd).utc().format('MM/DD/YYYY');

  const onRemoveClick = () => {
    onConfirm({
      title: 'Remove Billing Term',
      body: `Are you sure you want to remove this billing term of ${quantity} revisions, from ${termStart} - ${termEnd}?`,
      onConfirm: () => removeBillingTerm({ id }),
    });
  };

  return (
    <List.RowContainer>
      <List.Row key={id}>
        <List.RowTitle>
          <div>
            {termStart} - {termEnd}
            {isActive && <Badge color="green">Active</Badge>}
          </div>
          <LoaderButton
            type={BUTTON_TYPE.ANCHOR}
            size={BUTTON_SIZE.SMALL}
            icon={icons.times}
            isLoading={isPending}
            isDisabled={isPending}
            onClick={onRemoveClick}
          />
        </List.RowTitle>
        <List.RowDetails>
          <List.RowDetailsItem>
            {_.capitalize(type)} Usage
            <div className="margin-left-xs">
              <Badge color={usage <= quantity ? 'light-gray' : 'dark-red'}>
                {usage} / {quantity}
              </Badge>
            </div>
          </List.RowDetailsItem>
          <List.RowDetailsItem>
            Created By:
            <div className="margin-left-xs">
              <Badge color="light-gray">
                {createdBy}
              </Badge>
            </div>
          </List.RowDetailsItem>
          <List.RowDetailsItem>
            Type:
            <div className="margin-left-xs">
              <Badge color={type === BillingTermType.Revision ? 'light-green' : 'light-blue'}>
                {type.toUpperCase()}
              </Badge>
            </div>
          </List.RowDetailsItem>
        </List.RowDetails>
      </List.Row>
    </List.RowContainer>
  );
};

export default BillingTermRow;
